import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const MarklyPrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 py-8 md:py-12 mt-16">
        <div className="bg-white rounded-lg shadow-sm p-8 md:p-12">
          <header className="mb-8 border-b pb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Privacy Policy for Markly
            </h1>
            <p className="text-gray-600">
              Last updated: Feb 8, 2026
            </p>
          </header>

          <div className="prose prose-blue max-w-none text-gray-700 space-y-6">
            <section className="mb-8">
              <p className="mb-4">This Privacy Policy describes Our policies and procedures on the collection, use and disclosure of Your information when You use the Service and tells You about Your privacy rights and how the law protects You.</p>
              <p>We use Your Personal data to provide and improve the Service. By using the Service, You agree to the collection and use of information in accordance with this Privacy Policy.</p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Interpretation and Definitions</h2>

              <h3 className="text-xl font-medium text-gray-800 mb-3">Interpretation</h3>
              <p>The words of which the initial letter is capitalized have meanings defined under the following conditions. The following definitions shall have the same meaning regardless of whether they appear in singular or in plural.</p>

              <h3 className="text-xl font-medium text-gray-800 mb-3 mt-6">Definitions</h3>
              <p className="mb-2">For the purposes of this Privacy Policy:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Account</strong> means a unique account created for You to access our Service or parts of our Service.</li>
                <li><strong>Application</strong> refers to Markly, the software program provided by the Company.</li>
                <li><strong>Company</strong> (referred to as either "the Company", "We", "Us" or "Our" in this Agreement) refers to Apptek, a developer collective operating under this name without formal incorporation, based in Denmark.</li>
                <li><strong>Country</strong> refers to: Denmark</li>
                <li><strong>Device</strong> means any device that can access the Service such as a computer, a cellphone or a digital tablet.</li>
                <li><strong>Personal Data</strong> is any information that relates to an identified or identifiable individual.</li>
                <li><strong>Service</strong> refers to the Application.</li>
                <li><strong>Service Provider</strong> means any natural or legal person who processes the data on behalf of the Company. It refers to third-party companies or individuals employed by the Company to facilitate the Service, to provide the Service on behalf of the Company, to perform services related to the Service or to assist the Company in analyzing how the Service is used.</li>
                <li><strong>Usage Data</strong> refers to data collected automatically, either generated by the use of the Service or from the Service infrastructure itself (for example, Your searches)</li>
                <li><strong>You</strong> means the individual accessing or using the Service, or the company, or other legal entity on behalf of which such individual is accessing or using the Service, as applicable.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Collection and Using Your Personal Data</h2>

              <h3 className="text-xl font-medium text-gray-800 mb-3">Types of Data Collected</h3>

              <h4 className="text-lg font-medium text-gray-800 mb-2 mt-4">Personal Data</h4>
              <p className="mb-2">While using Our Service, We may ask You to provide Us with certain personally identifiable information that can be used to contact or identify You. Personally identifiable information may include, but is not limited to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Email address</li>
                <li>First name and last name</li>
                <li>Usage Data</li>
              </ul>

              <h4 className="text-lg font-medium text-gray-800 mb-2 mt-4">Usage Data and Information We Collect</h4>
              <p className="mb-2">We collect and process the following Personal Data:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Account Information:</strong> When You register or log in to Markly, we collect Your email address and other login credentials.</li>
                <li><strong>Usage Data:</strong> We store recent searches within the App to enhance user experience, along with inactivity information to perform the deletion of accounts, if deemed inactive (more below.) We do also collect, store and transmit images taken or uploaded by the user within our app. These are solely used for AI-generated assistance and OCR, and are only stored to show under the corresponding "recent scan" page.</li>
                <li><strong>Subscription Status:</strong> We store information regarding whether You have an active subscription (via Google Play or Apple App Store). We do not collect or store any payment or billing information.</li>
                <li><strong>Local Device Data:</strong> We store authentication tokens locally on Your device to enable auto-login.</li>
              </ul>

            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Third-Party Services</h2>
              <p className="mb-2">Markly integrates the following third-party tools and APIs:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Google ML Kit (on-device OCR processing)</li>
                <li>Google Gemini (AI-generated answers)</li>
                <li>Supabase (database and authentication service)</li>
              </ul>
              <p className="mt-2">Each third-party service is used in accordance with their own privacy policies and terms of use.</p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Legal Basis for Processing (GDPR)</h2>
              <p className="mb-2">We process your personal data under the following legal bases:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Performance of a contract:</strong> To provide core Service features (e.g., login, saved results).</li>
                <li><strong>Consent:</strong> For actions that require it, such as account creation.</li>
                <li><strong>Legitimate interest:</strong> To enhance service quality and functionality.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Your Rights (Under GDPR)</h2>
              <p className="mb-2">As an EU resident, you have the following rights:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Access to the Personal Data we hold about you</li>
                <li>Request correction or deletion of Your data</li>
                <li>Object to or restrict certain types of processing</li>
                <li>Data portability</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Delete Your Personal Data</h2>
              <p className="mb-4">You have the right to delete or request that We assist in deleting the Personal Data that We have collected about You. Our Service gives You the ability to delete certain information about You from within the Service.</p>
              <p className="mb-4">You may update, amend, or delete Your information at any time by signing in to Your Account, if you have one, and visiting the account settings section that allows you to manage Your personal information. You may also contact Us to request access to, correct, or delete any personal information that You have provided to Us.</p>
              <p>Please note, however, that We may need to retain certain information when we have a legal obligation or lawful basis to do so.</p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Data Retention</h2>
              <p>We retain your data only as long as necessary to fulfill the purposes outlined above or as required by law. Account data will be deleted upon user request or inactivity exceeding 24 months.</p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Disclosure of Your Personal Data</h2>

              <h3 className="text-xl font-medium text-gray-800 mb-3">Business Transactions</h3>
              <p className="mb-6">If the Company is involved in a merger, acquisition or asset sale, Your Personal Data may be transferred. We will provide notice before Your Personal Data is transferred and becomes subject to a different Privacy Policy.</p>

              <h3 className="text-xl font-medium text-gray-800 mb-3">Law enforcement</h3>
              <p className="mb-6">Under certain circumstances, the Company may be required to disclose Your Personal Data if required to do so by law or in response to valid requests by public authorities (e.g. a court or a government agency).</p>

              <h3 className="text-xl font-medium text-gray-800 mb-3">Other legal requirements</h3>
              <p className="mb-2">The Company may disclose Your Personal Data in the good faith belief that such action is necessary to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Comply with a legal obligation</li>
                <li>Protect and defend the rights or property of the Company</li>
                <li>Prevent or investigate possible wrongdoing in connection with the Service</li>
                <li>Protect the personal safety of Users of the Service or the public</li>
                <li>Protect against legal liability</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Security of Your Personal Data</h2>
              <p>The security of Your Personal Data is important to Us, but remember that no method of transmission over the Internet, or method of electronic storage is 100% secure. While We strive to use commercially acceptable means to protect Your Personal Data (through encryption, etc), We cannot guarantee its absolute security.</p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Children’s Privacy</h2>
              <p>Our Service is not intended for children under 13 years of age. We do not knowingly collect Personal Data from children without parental consent.</p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Links to Other Websites</h2>
              <p className="mb-4">Our Service may contain links to other websites that are not operated by Us. If You click on a third party link, You will be directed to that third party's site. We strongly advise You to review the Privacy Policy of every site You visit.</p>
              <p>We have no control over and assume no responsibility for the content, privacy policies or practices of any third party sites or services.</p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Changes to this Privacy Policy</h2>
              <p className="mb-4">We may update Our Privacy Policy from time to time. We will notify You of any changes by posting the new Privacy Policy on this page.</p>
              <p className="mb-4">We will let You know via email and/or a prominent notice on Our Service, prior to the change becoming effective and update the "Last updated" date at the top of this Privacy Policy.</p>
              <p>You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.</p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Contact Us</h2>
              <p className="mb-2">If You have any questions about this Privacy Policy, You can contact us:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>By email: markly@apptek.dev</li>
                <li>Through the app.</li>
              </ul>
            </section>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default MarklyPrivacyPolicy;
